import type { TabState } from '../../types';
import { getFiltered, formatSub, hasSubFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueByOpp, validReqAges, splitUserSCNames } from '../../utils/opportunities';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { AccountBySolutionChart } from '../charts/AccountBySolutionChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useRequests } from '../../context/RequestsContext';
import { useState } from 'react';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    requests
  } = useRequests();
  const [offeringTop, setOfferingTop] = useState(8);
  const [offeringTotal, setOfferingTotal] = useState(1);
  const all = getFiltered('vp', state, requests);
  const ip = all.filter(r => r.req_status === 'In Progress');
  const cls = all.filter(r => r.req_status === 'Closed');
  const onh = all.filter(r => r.req_status === 'On Hold');
  const can = all.filter(r => r.req_status === 'Cancelled');
  const scs = new Set(ip.flatMap(r => splitUserSCNames(r.user_sc_name))).size;
  const pipelineOppGnarr = sumUniqueByOpp(all, r => r.opp_gnarr);
  const pipelineOppGnarrTotal = sumUniqueByOpp(all, r => r.opp_gnarr_grand_total);
  const ages = validReqAges(all);
  const subLbl = formatSub(state.sub);
  const titleLbl = subLbl || state.r;
  const crumb = hasSubFilter(state.sub);

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
    cancelled: number;
  }> = {};
  all.forEach(r => {
    const key = showSub ? r.req_sub_region : r.opp_region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: r.opp_region,
      cancelled: 0
    };
    regMap[key].book++;
    if (r.req_status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += r.opp_gnarr;
    } else if (r.req_status === 'Closed') regMap[key].closed++;else if (r.req_status === 'On Hold') regMap[key].onhold++;else if (r.req_status === 'Cancelled') regMap[key].cancelled++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const ALLOWED_REGIONS = new Set(['AMER', 'EMEA', 'JAPAC', '']);
  const regRows = Object.entries(regMap).filter(([reg]) => showSub || ALLOWED_REGIONS.has(reg)).sort((a, b) => b[1].ip - a[1].ip);
  interface RegionRow {
    reg: string;
    ip: number;
    gnarr: number;
    closed: number;
    book: number;
    onhold: number;
    parent: string;
  }
  const regionData: RegionRow[] = regRows.map(([reg, d]) => ({
    reg,
    ip: d.ip,
    gnarr: d.gnarr,
    closed: d.closed,
    book: d.book,
    onhold: d.onhold,
    parent: d.parent
  }));
  const regionColumns: SortableColumn<RegionRow>[] = [{
    key: 'reg',
    label: 'Region',
    width: '14%',
    sortValue: r => r.reg,
    render: r => <>
          <span className="rdot" style={{
        background: RDOTS[r.reg] || '#888'
      }} data-id="element-1231" data-mp="3aryje5dj1xdd" />
          {r.reg || 'No DR'}
        </>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '26%',
    sortValue: r => r.ip,
    render: r => <>
          <div className="pbg" style={{
        width: 90
      }} data-id="element-1232" data-mp="3kol0cj2qg4a4">
            <div className="pf" style={{
          width: `${Math.round(r.ip / maxIP * 100)}%`
        }} data-id="element-1233" data-mp="3popjkgv0zd41" />
          </div>
          <strong data-id="element-1234" data-mp="3trcbehrp4y18">{r.ip}</strong>
        </>
  }, {
    key: 'gnarr',
    label: 'Pipeline GNARR',
    width: '16%',
    sortValue: r => r.gnarr,
    render: r => <>{fmtG(r.gnarr)}</>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '12%',
    sortValue: r => r.closed,
    render: r => <span style={{
      color: r.closed > 0 ? '#15803D' : '#344054',
      fontWeight: r.closed > 0 ? 600 : 400
    }} data-id="element-1235" data-mp="141v5bs5jyh48">{r.closed}</span>
  }, {
    key: 'book',
    label: 'Total',
    width: '12%',
    sortValue: r => r.book,
    render: r => <>{r.book}</>
  }, {
    key: 'onhold',
    label: 'On hold',
    width: '10%',
    sortValue: r => r.onhold,
    render: r => <>{r.onhold}</>
  }, {
    key: 'parent',
    label: 'Sub-region',
    width: '10%',
    sortValue: r => r.parent,
    render: r => showSub ? <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-1236" data-mp="ge3k1z21atqt">{r.parent}</span> : <>—</>
  }];

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(r => {
    if (!cqMap[r.opp_close_q]) cqMap[r.opp_close_q] = {
      n: 0,
      g: 0
    };
    cqMap[r.opp_close_q].n++;
    cqMap[r.opp_close_q].g += r.opp_gnarr;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-1237" data-mp="2s1p6csjhn50k">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(6, 1fr)'
    }} data-id="element-1238" data-mp="2ryb21er9klqh">
       <MetricCard label="Total requests" value={ip.length + cls.length} sub={`Closed + In progress `} colorClass="c-green" data-id="element-1239" data-mp="2wyytg1jen4l8" />
       <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-1240" data-mp="2j22301777p4v" />
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-1241" data-mp="2nwzdlck4m6jv" />
        <MedianAgeCard ages={ages} data-id="element-1242" data-mp="17mwxkpfonchk" />
        <MetricCard label="GNARR (DX) Influenced" value={fmtG(pipelineOppGnarr)} sub={`Closed & In-progress requests`} colorClass="c-green" data-id="element-1243" data-mp="1cjx5l5w3epbb" />
        <MetricCard label="GNARR (Total) Influenced" value={fmtG(pipelineOppGnarrTotal)} sub={`Closed & In-progress requests`} colorClass="c-green" data-id="element-1244" data-mp="3e81awlsuh09s" />
      </div> 

      {/* Charts row: Offering (left) + Trend (right) */}
      <div style={{
      display: 'flex',
      gap: 12
    }} data-id="element-1245" data-mp="34gna8ap5puf2">
        <div style={{
        flex: 1,
        minWidth: 0
      }} data-id="element-1246" data-mp="30gczkkqf6ftv">
          <SectionHeader title="Accounts by solution" data-id="element-1247" data-mp="30flew4yd22id" />
          <div className="card" style={{
          minHeight: 260
        }} data-id="element-1248" data-mp="2vj4h0j1d09s3">
            <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4
          }} data-id="element-1249" data-mp="2qji3s9lj6tja">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 9,
              color: '#667085'
            }} data-id="element-1250" data-mp="2ljdp8p46o8vp">
                <span data-id="element-1251" data-mp="2hjljsla36iuk">Top</span>
                <input type="range" min={1} max={offeringTotal} value={offeringTop} onChange={e => setOfferingTop(Number(e.target.value))} style={{
                width: 80,
                accentColor: '#3B82F6'
              }} data-id="element-1252" data-mp="2hkmz51y4xri3" />
                <span style={{
                minWidth: 20,
                textAlign: 'right'
              }} data-id="element-1253" data-mp="1xpll2m8w97vw">{offeringTop + 1}</span>
              </div>
            </div>
            <AccountBySolutionChart requests={all} top={offeringTop} onTopChange={setOfferingTop} onTotalChange={setOfferingTotal} chartHeight={224} data-id="element-1254" data-mp="22mc4njjwqlm2" />
          </div>
        </div>
        <div style={{
        flex: 1,
        minWidth: 0
      }} data-id="element-1255" data-mp="2bf5mnoqqnz3m">
          <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-1257" data-mp="1esaml3v36ihi">{subLbl}</span>}</>} data-id="element-1256" data-mp="2bi1i96f46bow" />
          <div className="card" style={{
          minHeight: 260
        }} data-id="element-1258" data-mp="1jpavdmjblzcb">
            <TrendChart region={state.r} sub={state.sub} status={state.status} requests={requests.filter(r => r.req_status === 'In Progress' || r.req_status === 'Closed')} data-id="element-1259" data-mp="1nqcve5l2affc" />
          </div>
        </div>
      </div>

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-1261" data-mp="3431fgid6t6v4">{subLbl}</span>}</>} data-id="element-1260" data-mp="2z7vyzz2k0kef" />
      <div className="tw" data-id="element-1262" data-mp="2friwcizqakj4">
        <div className="sub" data-id="element-1263" data-mp="2fsu04it6bsa8">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <SortableTable columns={regionColumns} data={regionData} rowKey={r => r.reg} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={r => onDrillRegion(r.parent || r.reg, showSub ? r.reg : '')} data-id="element-1264" data-mp="2ol5todjl3vuf" />
      </div>

      {/* Book status + CQ grid */}
      {/* <div className="br2">
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 8px' }}>
            <div className="shb" />
            <span className="sht">Book status mix</span>
          </div>
          <div className="book-bar">
            {segs.map(s => (
              <div key={s.l} style={{ width: `${Math.round(s.v / total * 100)}%`, height: '100%', background: s.c }} />
            ))}
          </div>
          <div className="bleg">
            {segs.map(s => (
              <span key={s.l}>
                <span className="bldot" style={{ background: s.c }} />
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 6px' }}>
            <div className="shb" />
            <span className="sht">In progress by close quarter</span>
          </div>
          <div className="cqg">
            {cqShow.map((q, i) => (
              <div key={q} className="cqc">
                <div className="cql">{q}</div>
                <div className="cqn">{cqMap[q].n}</div>
                <div className="cqs">in-progress deals</div>
                <div className="cqs">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{ background: qColors[i] || '#4f9cf9' }} />
              </div>
            ))}
          </div>
        </div>
       </div> */}

    </div>;
}